import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

function waitForImages(doc: Document): Promise<void> {
  const images = Array.from(doc.images);
  if (images.length === 0) return Promise.resolve();
  return Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    )
  ).then(() => undefined);
}

export function invoicePdfFilename(clinicName: string, period: string): string {
  const slug = clinicName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `Factura-${period}-${slug || 'clinica'}.pdf`;
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function htmlToPdfBlob(html: string): Promise<Blob> {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.srcdoc = html;
  Object.assign(iframe.style, {
    position: 'fixed',
    left: '-12000px',
    top: '0',
    width: '794px',
    height: '1123px',
    border: '0',
    pointerEvents: 'none',
  });
  document.body.appendChild(iframe);

  try {
    await new Promise<void>((resolve, reject) => {
      iframe.onload = () => resolve();
      iframe.onerror = () => reject(new Error('pdf-frame'));
      window.setTimeout(() => resolve(), 1500);
    });

    const doc = iframe.contentDocument;
    const body = doc?.body;
    if (!doc || !body) throw new Error('pdf-frame');

    body.style.boxSizing = 'border-box';
    body.style.width = '210mm';
    body.style.padding = '12mm 12mm 14mm 12mm';
    body.style.background = '#ffffff';
    await waitForImages(doc);
    iframe.style.height = `${Math.max(body.scrollHeight, body.offsetHeight, 1123)}px`;
    await new Promise((resolve) => window.setTimeout(resolve, 40));

    const canvas = await html2canvas(body, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: 794,
      scrollX: 0,
      scrollY: 0,
    });

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
    const img = canvas.toDataURL('image/jpeg', 0.93);

    let offset = 0;
    let remaining = imgHeight;
    pdf.addImage(img, 'JPEG', 0, offset, pageWidth, imgHeight, undefined, 'FAST');
    remaining -= pageHeight;
    while (remaining > 1) {
      offset -= pageHeight;
      pdf.addPage();
      pdf.addImage(img, 'JPEG', 0, offset, pageWidth, imgHeight, undefined, 'FAST');
      remaining -= pageHeight;
    }
    return pdf.output('blob');
  } finally {
    iframe.remove();
  }
}

export async function downloadHtmlAsPdf(html: string, filename: string): Promise<void> {
  const blob = await htmlToPdfBlob(html);
  saveBlob(blob, filename);
}
